import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Zap, AlertTriangle, CheckCircle2 } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * One-click "fill every empty block" tool. Runs a dry-run first so ops
 * always sees exactly what will change before writing. Rotates through
 * music categories that have >=1 CDN-ready asset. Blocks that already
 * point at an asset OR category are left alone.
 */
export default function BackfillEmptyDialog({ open, onClose, channels }) {
  const [selected, setSelected] = useState(() => new Set());
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(new Set(channels.map((c) => c.id)));
      setPreview(null);
    }
  }, [open, channels]);

  const runCall = useCallback(async (dry_run) => {
    const setter = dry_run ? setPreviewing : setApplying;
    setter(true);
    try {
      const r = await api.post("/schedule/backfill-empty-blocks", {
        channel_ids: Array.from(selected),
        dry_run,
      });
      setPreview(r.data);
      if (!dry_run) {
        toast.success(`Backfilled ${r.data.total_blocks} blocks`);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Backfill failed");
    } finally {
      setter(false);
    }
  }, [selected]);

  const toggleChan = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const canApply = preview && preview.dry_run && preview.total_blocks > 0 && !applying;
  const rows = preview ? Object.entries(preview.per_channel) : [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-2xl" data-testid="backfill-empty-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Zap className="w-5 h-5 text-fuchsia-500" />
            Fill empty blocks
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground font-mono">
            Every schedule block that has no asset AND no category falls through to the demo stream. This tool assigns a rotation of music categories to those blocks so nothing plays wrong. Blocks that already point at an asset or a category are untouched.
          </DialogDescription>
        </DialogHeader>

        {/* Channel selection */}
        <div className="border border-border rounded-sm p-3 max-h-40 overflow-y-auto scrollbar-thin">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
            Channels · pick which ones to fill
          </div>
          <div className="grid grid-cols-2 gap-1">
            {channels.map((c) => (
              <label
                key={c.id}
                className="flex items-center gap-2 text-xs cursor-pointer hover:bg-accent/40 px-2 py-1 rounded-sm"
                data-testid={`backfill-channel-${c.id}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(c.id)}
                  onChange={() => toggleChan(c.id)}
                  className="accent-fuchsia-500"
                />
                <span className="truncate">{c.name}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Preview call */}
        <Button
          onClick={() => runCall(true)}
          disabled={previewing || applying || selected.size === 0}
          data-testid="backfill-preview-btn"
          variant="outline"
          className="rounded-sm h-9 gradient-stroke font-display text-sm"
        >
          {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {preview ? "Refresh preview" : "Preview changes"}
        </Button>

        {/* Result */}
        {preview && (
          <div data-testid="backfill-preview" className="text-xs border border-border rounded-sm p-3 space-y-2 max-h-80 overflow-y-auto scrollbar-thin">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest">
              {preview.total_blocks > 0 ? (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  <span>Would update <b className="text-foreground">{preview.total_blocks}</b> blocks</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">No empty blocks — nothing to do</span>
                </>
              )}
            </div>
            {preview.total_blocks > 0 && (
              <>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Per channel</div>
                  {rows.filter(([, v]) => v.total > 0).map(([ch, v]) => (
                    <div key={ch} className="pl-2 py-1 border-l border-border">
                      <div className="font-mono">{ch} · <span className="text-muted-foreground">{v.total} total</span></div>
                      <div className="text-[10px] text-muted-foreground pl-2">
                        {DAYS.map((d) => v.per_weekday[d] > 0 ? `${d}:${v.per_weekday[d]}` : null).filter(Boolean).join("  ·  ")}
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Rotation</div>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(preview.per_category).map(([n, c]) => (
                      <span key={n} className="px-1.5 py-0.5 rounded-sm bg-accent text-[10px] font-mono">
                        {n} · {c}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {preview && preview.total_blocks > 0 && (
          <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-sm bg-amber-500/10 border border-amber-500/30 text-amber-500 dark:text-amber-300">
            <AlertTriangle className="w-3.5 h-3.5" />
            The write is atomic per block and only touches empty rows. Live pipes are not interrupted.
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button
            onClick={async () => {
              await runCall(false);
              onClose();
            }}
            disabled={!canApply}
            data-testid="backfill-apply-btn"
            className="tenant-btn rounded-sm"
          >
            {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Apply · {preview?.total_blocks || 0}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
